/**
 * apps/battle/client/src/terminal/screens/AccountScreen.tsx
 *
 * Phase 4.2 M7 — account management screen.
 *
 *   GET    /api/account/me     → { email, displayName, emailVerifiedAt, displayNameChangedAt, canChangeDisplayName, daysUntilChange }
 *   PUT    /api/account/me     → update display name (rate-limited to 1/30d server-side)
 *   POST   /api/auth/request-verify  → resend verification email
 *   DELETE /api/account        → soft-delete + immediate anonymise + logout
 */
import React, { useEffect, useState } from 'react';
import { api, ApiError } from '../../api.js';
import { Spinner, ErrorBanner } from '../../widgets.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';

interface BillingInfo {
  tier: 'FREEMIUM' | 'STANDARD' | 'PLUS';
  capType: 'lifetime' | 'monthly';
  capLimit: number;
  used: number;
  remaining: number;
  periodLabel: string | null;
  source: 'STRIPE' | 'MANUAL' | null;
  expiry: string | null;
}

interface AccountMe {
  id: string;
  email: string;
  displayName: string;
  emailVerifiedAt: string | null;
  displayNameChangedAt: string | null;
  canChangeDisplayName: boolean;
  daysUntilChange: number;
  // M12h — present for students; null for sme/admin (never gated).
  billing: BillingInfo | null;
}

/** "1 day" (singular) vs "N days" (plural). */
function daysLabel(n: number): string {
  return n === 1 ? '1 day' : `${n} days`;
}

interface AccountScreenProps {
  /** Called after the user successfully deletes their account or signs out. */
  onLoggedOut: () => void;
  /** Called when the user wants to go back to the title screen. */
  onBackToTitle: () => void;
  /** M12h — one-line banner shown on return from Stripe (success/cancel). */
  notice?: string | null;
  /** M12h — bump to force a re-fetch of /api/account/me (absorbs webhook lag). */
  refreshSignal?: number;
}

export function AccountScreen({ onLoggedOut, onBackToTitle, notice, refreshSignal }: AccountScreenProps) {
  const [me, setMe] = useState<AccountMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit display name
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<string | null>(null);

  // Resend verification
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  // Delete-account confirmation
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  // M12h — billing (checkout / portal redirects). `billingBusy` holds the action
  // in flight ('STANDARD' | 'PLUS' | 'portal') so we can disable the right button.
  const [billingBusy, setBillingBusy] = useState<'STANDARD' | 'PLUS' | 'portal' | null>(null);
  const [billingMsg, setBillingMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<AccountMe>('/api/account/me')
      .then((r) => { if (!cancelled) { setMe(r); setNewName((prev) => (editing ? prev : r.displayName)); } })
      .catch(() => { if (!cancelled) setError('Could not load account details.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // refreshSignal bumps force a re-fetch (M12h — webhook-lag absorption).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  async function saveName() {
    if (!me) return;
    if (!newName.trim() || newName.trim().length < 2) { setNameMsg('Name must be at least 2 characters.'); return; }
    if (newName.trim() === me.displayName) { setEditing(false); return; }
    setSavingName(true);
    setNameMsg(null);
    try {
      const updated = await api.put<AccountMe>('/api/account/me', { displayName: newName.trim() });
      setMe(updated);
      setEditing(false);
      setNameMsg('Display name updated.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const body = err.body as { daysUntilChange?: number } | undefined;
        setNameMsg(`You can change your display name again in ${body?.daysUntilChange ?? '~30'} days.`);
      } else if (err instanceof ApiError && err.status === 422) {
        setNameMsg('That display name is not allowed.');
      } else {
        setNameMsg('Could not update display name.');
      }
    } finally {
      setSavingName(false);
    }
  }

  async function resendVerify() {
    setResendStatus('sending');
    setResendMsg(null);
    try {
      await api.post('/api/auth/request-verify', {});
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('error');
      if (err instanceof ApiError && err.status === 429) {
        setResendMsg('Please wait a few minutes before requesting another email.');
      } else {
        setResendMsg('Could not send verification email.');
      }
    }
  }

  async function startCheckout(tier: 'STANDARD' | 'PLUS') {
    setBillingBusy(tier);
    setBillingMsg(null);
    try {
      const { url } = await api.post<{ url: string }>('/api/billing/checkout', { tier });
      window.location.assign(url);
    } catch (err) {
      setBillingBusy(null);
      if (err instanceof ApiError) {
        if (err.status === 503) setBillingMsg("Billing isn't enabled yet. Please try again later.");
        else if (err.status === 409) { setBillingMsg('You already have an active subscription — refreshing…'); void reloadBilling(); }
        else if (err.status === 403) setBillingMsg('Verify your email to subscribe.');
        else if (err.status === 400) setBillingMsg('That plan is unavailable. Please try again.');
        else setBillingMsg('Could not start checkout. Please try again.');
      } else {
        setBillingMsg('Could not start checkout. Please try again.');
      }
    }
  }

  async function openPortal() {
    setBillingBusy('portal');
    setBillingMsg(null);
    try {
      const { url } = await api.post<{ url: string }>('/api/billing/portal', {});
      window.location.assign(url);
    } catch (err) {
      setBillingBusy(null);
      if (err instanceof ApiError) {
        if (err.status === 400) setBillingMsg('No billing account found yet. Subscribe first to manage billing.');
        else if (err.status === 503) setBillingMsg("Billing isn't enabled yet. Please try again later.");
        else setBillingMsg('Could not open the billing portal. Please try again.');
      } else {
        setBillingMsg('Could not open the billing portal. Please try again.');
      }
    }
  }

  async function reloadBilling() {
    try {
      const fresh = await api.get<AccountMe>('/api/account/me');
      setMe(fresh);
    } catch { /* non-fatal */ }
  }

  async function handleDelete() {
    if (deleteText !== 'DELETE') { setDeleteErr('Type DELETE to confirm.'); return; }
    setDeleting(true);
    setDeleteErr(null);
    try {
      await api.delete('/api/account');
      onLoggedOut();
    } catch {
      setDeleteErr('Could not delete account. Please try again.');
      setDeleting(false);
    }
  }

  async function handleLogout() {
    try { await api.post('/api/auth/logout', {}); } catch { /* ignore */ }
    onLoggedOut();
  }

  const keybar: KeyBarItem[] = [
    { label: 'Esc', description: 'back to game', onActivate: onBackToTitle },
    { label: 'L', description: 'sign out', onActivate: handleLogout },
  ];

  return (
    <TerminalFrame
      title={<>ECONQUEST &nbsp;&nbsp;─&nbsp;&nbsp;ACCOUNT</>}
      keybar={<KeyBar items={keybar} />}
    >
      <div style={{ maxWidth: 560, margin: '0 auto', paddingTop: 16, color: '#cdd5e0' }}>
        {notice && (
          <div style={{
            marginBottom: 12,
            padding: '10px 12px',
            background: '#0f1f17',
            border: '1px solid #1f3a2b',
            borderRadius: 6,
            color: '#a7e8c5',
            fontSize: 13,
          }}>{notice}</div>
        )}
        {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spinner size={20} /></div>}
        {error && <ErrorBanner msg={error} />}
        {me && (
          <>
            {/* Email */}
            <Section title="Email">
              <div style={{ color: '#e6ebf2' }}>{me.email}</div>
              <div style={{ marginTop: 4, fontSize: 13 }}>
                {me.emailVerifiedAt ? (
                  <span style={{ color: '#a7e8c5' }}>✓ Verified</span>
                ) : (
                  <span style={{ color: '#e8c5a7' }}>○ Not verified</span>
                )}
              </div>
              {!me.emailVerifiedAt && (
                <div style={{ marginTop: 8 }}>
                  <button className="btn" type="button" onClick={resendVerify} disabled={resendStatus === 'sending'}>
                    {resendStatus === 'sending' ? <Spinner size={14} /> : 'Send verification email'}
                  </button>
                  {resendStatus === 'sent' && <span style={{ marginLeft: 8, color: '#a7e8c5', fontSize: 13 }}>Sent.</span>}
                  {resendStatus === 'error' && resendMsg && <span style={{ marginLeft: 8, color: '#e8a7a7', fontSize: 13 }}>{resendMsg}</span>}
                </div>
              )}
            </Section>

            {/* Display name */}
            <Section title="Display name">
              {editing ? (
                <div>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    maxLength={32}
                    style={{ width: '100%' }}
                  />
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" type="button" onClick={saveName} disabled={savingName}>
                      {savingName ? <Spinner size={14} /> : 'Save'}
                    </button>
                    <button className="btn" type="button" onClick={() => { setEditing(false); setNewName(me.displayName); setNameMsg(null); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <span style={{ color: '#e6ebf2' }}>{me.displayName}</span>
                  <button
                    className="btn"
                    type="button"
                    style={{ marginLeft: 12 }}
                    onClick={() => setEditing(true)}
                    disabled={!me.canChangeDisplayName}
                    title={me.canChangeDisplayName ? '' : `You can change your display name again in ${daysLabel(me.daysUntilChange)}.`}
                  >
                    Edit
                  </button>
                  {!me.canChangeDisplayName && me.daysUntilChange > 0 && (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#97a3b6' }}>
                      Can be changed again in {daysLabel(me.daysUntilChange)}.
                    </div>
                  )}
                </div>
              )}
              {nameMsg && <div style={{ marginTop: 8, fontSize: 13, color: '#97a3b6' }}>{nameMsg}</div>}
            </Section>

            {/* Subscription (M12h) */}
            {me.billing && (
              <Section title="Subscription">
                {(() => {
                  const b = me.billing;
                  const isPaid = b.tier === 'STANDARD' || b.tier === 'PLUS';
                  const tierColor = b.tier === 'FREEMIUM' ? '#97a3b6' : '#a7e8c5';
                  const atCap = b.used >= b.capLimit;
                  const nearCap = !atCap && b.remaining <= Math.max(1, Math.ceil(b.capLimit * 0.1));
                  const usageColor = atCap ? '#e8a7a7' : nearCap ? '#e8c5a7' : '#cdd5e0';
                  const usageLine =
                    b.capType === 'lifetime'
                      ? `Graded attempts: ${b.used} / ${b.capLimit} (lifetime)`
                      : `Graded attempts: ${b.used} / ${b.capLimit} this month${b.periodLabel ? ` · resets ${b.periodLabel}` : ''}`;
                  return (
                    <>
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: '#97a3b6' }}>PLAN: </span>
                        <span style={{ color: tierColor, fontWeight: 600 }}>{b.tier}</span>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: usageColor }}>{usageLine}</div>

                      {!isPaid ? (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => { void startCheckout('STANDARD'); }}
                              disabled={!me.emailVerifiedAt || billingBusy !== null}
                            >
                              {billingBusy === 'STANDARD' ? <Spinner size={14} /> : 'Subscribe to STANDARD — S$9/mo'}
                            </button>
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => { void startCheckout('PLUS'); }}
                              disabled={!me.emailVerifiedAt || billingBusy !== null}
                            >
                              {billingBusy === 'PLUS' ? <Spinner size={14} /> : 'Subscribe to PLUS — S$19/mo'}
                            </button>
                          </div>
                          {!me.emailVerifiedAt && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#e8c5a7' }}>
                              Verify your email to subscribe (see the Email section above).
                            </div>
                          )}
                        </div>
                      ) : b.source === 'STRIPE' ? (
                        <div style={{ marginTop: 12 }}>
                          <button
                            className="btn btn-primary"
                            type="button"
                            onClick={() => { void openPortal(); }}
                            disabled={billingBusy !== null}
                          >
                            {billingBusy === 'portal' ? <Spinner size={14} /> : 'Manage billing'}
                          </button>
                          <div style={{ marginTop: 6, fontSize: 12, color: '#97a3b6' }}>
                            Upgrade, downgrade, update your card, or cancel — all via Stripe.
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginTop: 12, fontSize: 12, color: '#97a3b6' }}>
                          Your plan was granted by an administrator. Contact support to change or cancel it.
                        </div>
                      )}
                      {billingMsg && <div style={{ marginTop: 8, fontSize: 13, color: '#e8a7a7' }}>{billingMsg}</div>}
                    </>
                  );
                })()}
              </Section>
            )}

            {/* Sign out */}
            <Section title="Session">
              <button className="btn" type="button" onClick={handleLogout}>Sign out</button>
            </Section>

            {/* Danger zone */}
            <Section title="Danger zone" danger>
              {!confirmDelete ? (
                <button className="btn btn-danger" type="button" onClick={() => setConfirmDelete(true)}>
                  Delete my account
                </button>
              ) : (
                <div>
                  <p style={{ color: '#e8a7a7', marginTop: 0, fontSize: 13, lineHeight: 1.5 }}>
                    This permanently deletes your account. Your email and display name will be
                    immediately anonymised. Battle history is retained anonymously for analytics.
                    <strong> This cannot be undone.</strong>
                  </p>
                  <p style={{ fontSize: 13 }}>Type <code style={{ background: '#1a2030', padding: '2px 6px', borderRadius: 3 }}>DELETE</code> to confirm:</p>
                  <input
                    type="text"
                    value={deleteText}
                    onChange={(e) => setDeleteText(e.target.value)}
                    placeholder="DELETE"
                    style={{ width: '100%' }}
                  />
                  {deleteErr && <div style={{ marginTop: 8, fontSize: 13, color: '#e8a7a7' }}>{deleteErr}</div>}
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button className="btn btn-danger" type="button" onClick={handleDelete} disabled={deleting || deleteText !== 'DELETE'}>
                      {deleting ? <Spinner size={14} /> : 'Permanently delete'}
                    </button>
                    <button className="btn" type="button" onClick={() => { setConfirmDelete(false); setDeleteText(''); setDeleteErr(null); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </Section>
          </>
        )}
      </div>
    </TerminalFrame>
  );
}

function Section({ title, children, danger }: { title: string; children: React.ReactNode; danger?: boolean }) {
  return (
    <div style={{
      marginTop: 16,
      padding: 16,
      background: danger ? '#1a0f0f' : '#0f141c',
      border: `1px solid ${danger ? '#3a1f1f' : '#1f2937'}`,
      borderRadius: 6,
    }}>
      <div style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: danger ? '#e8a7a7' : '#97a3b6',
        marginBottom: 8,
      }}>{title}</div>
      {children}
    </div>
  );
}
